import { z } from 'zod';
import { communicationId, communicationSchema } from './communication.js';
import { isRendered, renderMail } from './render.js';
import { redactSecrets } from './sanitize.js';
import { authoredTemplate } from './templates.js';
/**
 * The HR email composer's payloads (#62, PLAN §7.5): one schema per payload, parsed by the
 * form and by the API.
 *
 * What HR writes is a subject and a body. Everything else — the recipient, the sender, the
 * merge values — is decided by the server, so the client never holds a record to template
 * against and cannot address a message anywhere but the person's own address on file.
 *
 * Strict, and with no attachment field: an object carrying `attachments` is refused, not
 * trimmed. PLAN §7.5 says links, not attachments, and the payload has no place for a file.
 */
/**
 * The merge fields the composer offers. `portalLink` and `accessCode` are deliberately absent:
 * token-bearing mail is not sent by this path in Phase 2 (#61 decision 5), and the link and
 * the code travel by separate channels. `dueDate` and `items` belong to request templates,
 * not to a free-form message. A token outside this list does not resolve, and is named.
 */
export const composerMergeTokens = ['firstName', 'preferredName', 'fullName', 'hrContact'];
const subject = z
    .string()
    .trim()
    .min(1, 'Add a subject')
    .max(200)
    .refine((value) => !/[\r\n]/.test(value), 'The subject must be one line');
const body = z.string().trim().min(1, 'Write a message').max(16000);
/** Preview: exactly what HR typed, nothing about who it goes to. */
export const composeMessageSchema = z.object({ subject, body }).strict();
/** Send: the same, plus the one id that makes a retried click the same message. */
export const sendCommunicationRequestSchema = z.object({ operationId: z.string().uuid(), subject, body }).strict();
export const composePreviewSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('rendered'), subject: z.string().min(1), html: z.string().min(1), text: z.string().min(1) }).strict(),
    /** Merge fields that cannot be filled for this person, in code-unit order. Nothing is sent. */
    z.object({ kind: z.literal('unresolved'), unresolved: z.array(z.string()).min(1) }).strict(),
    /** The message would carry something the stored copy must not — today, a portal link. */
    z.object({ kind: z.literal('refused'), reason: z.string().min(1) }).strict(),
]);
export const sendCommunicationResponseSchema = z.object({ communication: communicationSchema }).strict();
/** The Communication a composed message is recorded under. Derived, so a retry meets its own record. */
export function composerCommunicationId(operationId) {
    return communicationId(`composer:${operationId}`);
}
/** The composer's merge values for one person. Only the offered tokens, and only when there is a value. */
export function composerMergeContext(person, hrContact) {
    const given = person.name.givenName.trim();
    const family = person.name.familyName.trim();
    const preferred = person.name.preferredName.trim() || given;
    return {
        ...(given === '' ? {} : { firstName: given }),
        ...(preferred === '' ? {} : { preferredName: preferred }),
        ...(`${given} ${family}`.trim() === '' ? {} : { fullName: `${given} ${family}`.trim() }),
        ...(hrContact === null || hrContact.trim() === '' ? {} : { hrContact: hrContact.trim() }),
    };
}
/**
 * Why an authored message will NEVER be sent from its record (#62). Each starts with a stable
 * code, so an operator view or a test can tell these apart from "waiting for mail to be
 * configured", which leaves the message queued. A parked Communication is final: HR writes the
 * message again.
 */
export const authoredParkReasons = {
    recipientChanged: 'recipient-changed: the email address on file changed after this message was queued. Write the message again to send it to the new address.',
    noRecipient: 'no-recipient: the person has no usable email address. Add one and write the message again.',
    storedRedacted: 'stored-redacted: the stored copy differs from the message that would be delivered, so it cannot be sent from its record.',
};
export const PORTAL_LINK_REFUSAL = 'A portal link cannot be pasted into a message. Send portal access from the Portal step on the person’s record, where the link and the access code go separately.';
/**
 * Render a composed message against the server-built context. Pure.
 *
 * A known token the composer does not offer (`portalLink`, `accessCode`, `items`, `dueDate`)
 * is left out of the context on purpose, so it is reported unresolved exactly like a typo.
 * A body that the stored-copy redaction would change is refused outright: the Communication
 * must hold the bytes that are delivered, and a pasted `?t=` link is a bearer token.
 */
export function renderComposedMessage(message, context) {
    const parsed = composeMessageSchema.parse({ subject: message.subject, body: message.body });
    const offered = Object.fromEntries(Object.entries(context).filter(([key]) => composerMergeTokens.includes(key)));
    const rendered = renderMail(authoredTemplate(parsed.subject, parsed.body), offered);
    if (!isRendered(rendered))
        return { kind: 'unresolved', unresolved: [...rendered.unresolved] };
    const probe = redactSecrets({ to: 'probe@example.invalid', templateId: 'custom', ...rendered });
    if (probe.redacted)
        return { kind: 'refused', reason: PORTAL_LINK_REFUSAL };
    return { kind: 'rendered', ...rendered };
}
//# sourceMappingURL=composer.js.map